import { Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { Bank_Account, Bank_Movement } from 'src/app/models/RestModels';
import { BaseComponent } from '../base/base.component';
import { ExcelUtils } from 'src/app/classes/ExcelUtils';
import { Utils } from 'src/app/classes/Utils';

interface MovementOrigin
{
	purchase_id?: number;
	bill_id?: number;
	order_id?: number;
}

@Component({
	selector: 'app-list-bank-movement',
	templateUrl: './list-bank-movement.component.html',
	styleUrls: ['./list-bank-movement.component.css']
})
export class ListBankMovementComponent extends BaseComponent implements OnInit
{
	bank_account_id: number | null = null;
	bank_account: Bank_Account | null = null;
	movement_list: Bank_Movement[] = [];
	movement_origins: Record<number, MovementOrigin[]> = {};

	selected_movement: Bank_Movement | null = null;
	selected_movement_info: any = null;
	show_detail_modal: boolean = false;
	loading_detail: boolean = false;

	bank_account_list: Bank_Account[] = [];

	ngOnInit()
	{
		this.setTitle('Movimientos Bancarios');
		this.path = '/list-bank-movement';

		this.sink = this.route.queryParamMap
		.pipe(
			mergeMap((params) =>
			{
				this.is_loading = true;
				this.bank_account_id = params.has('bank_account_id')
					? Number(params.get('bank_account_id'))
					: null;

				let search: any = {
					limit: 99999,
					sort_order: ['id_DESC']
				};

				if (this.bank_account_id)
				{
					search.eq = { bank_account_id: this.bank_account_id };
				}

				let requests: any = {
					movements: this.rest.bank_movement.search(search),
					bank_accounts: this.rest.bank_account.search({limit: 9999}),
					expense_payments: this.rest.payment_info.search({eq: {type: 'expense'}, limit: 99999}),
					income_payments: this.rest.payment_info.search({eq: {type: 'income'}, limit: 99999}),
				};

				if (this.bank_account_id)
				{
					requests.bank_account = this.rest.bank_account.get(this.bank_account_id);
				}

				return forkJoin(requests);
			})
		)
		.subscribe({
			next: (response: any) =>
			{
				this.movement_list = response.movements.data;
				this.bank_account_list = response.bank_accounts.data;

				if (response.bank_account)
				{
					this.bank_account = response.bank_account;
					this.setTitle('Movimientos Bancarios - ' + (this.bank_account.alias || this.bank_account.name || 'Cuenta #' + this.bank_account_id));
				}

				const allPayments: any[] = [
					...response.expense_payments.data,
					...response.income_payments.data,
				];

				for (let payment of allPayments)
				{
					for (let bmi of payment.movements)
					{
						const mid = bmi.bank_movement.id;
						if (!mid) continue;

						const origins: MovementOrigin[] = [];

						if (bmi.bank_movement_bills)
						{
							for (let bmb of bmi.bank_movement_bills)
							{
								origins.push({
									bill_id: bmb.bill?.id || undefined,
									purchase_id: bmb.bill?.purchase_id || undefined,
								});
							}
						}

						if (bmi.bank_movement_orders)
						{
							for (let bmo of bmi.bank_movement_orders)
							{
								origins.push({
									order_id: bmo.order_id || undefined,
								});
							}
						}

						if (origins.length)
						{
							this.movement_origins[mid] = origins;
						}
					}
				}

				this.is_loading = false;
			},
			error: (error: any) =>
			{
				this.showError(error);
				this.is_loading = false;
			}
		});
	}

	viewMovementDetail(movement: Bank_Movement)
	{
		if (!movement.id) return;

		const paymentId = movement.payment_id;

		this.selected_movement = movement;
		this.selected_movement_info = null;
		this.loading_detail = true;
		this.show_detail_modal = true;

		if (paymentId)
		{
			this.subs.sink = this.rest.payment_info.get(paymentId)
			.subscribe({
				next: (pi: any) =>
				{
					const bmi = pi.movements?.find(m => m.bank_movement.id === movement.id);
					if (bmi)
					{
						const info: any = {
							bank_movement: bmi.bank_movement,
							bank_account: bmi.bank_account,
							provider: bmi.provider,
							bank_movement_bills: bmi.bank_movement_bills,
							bank_movement_orders: bmi.bank_movement_orders,
						};
						this.selected_movement_info = info;
					}
					else
					{
						const info: any = {
							bank_movement: movement,
							bank_movement_bills: [],
							bank_movement_orders: [],
						};
						this.selected_movement_info = info;
					}
					this.loading_detail = false;
				},
				error: (error: any) =>
				{
					this.showError(error);
					this.loading_detail = false;
				}
			});
		}
		else
		{
			const info: any = {
				bank_movement: movement,
				bank_movement_bills: [],
				bank_movement_orders: [],
			};
			this.selected_movement_info = info;
			this.loading_detail = false;
		}
	}

	getOrigins(movement: Bank_Movement): MovementOrigin[]
	{
		return this.movement_origins[movement.id] || [];
	}

	getTransactionTypeLabel(type: string): string
	{
		const labels: Record<string, string> = {
			'CASH': 'Efectivo',
			'TRANSFER': 'Transferencia',
			'CHECK': 'Cheque',
			'CREDIT_CARD': 'T. Crédito',
			'DEBIT_CARD': 'T. Débito',
			'DIGITAL_WALLET': 'Billetera digital',
			'PAYPAL': 'PayPal',
			'COUPON': 'Cupón',
			'DISCOUNT': 'Descuento',
			'RETURN_DISCOUNT': 'Devolución',
		};
		return labels[type] || type;
	}

	exportExcel()
	{
		let rows: Object[] = [];

		for (let m of this.movement_list)
		{
			const origins = this.getOrigins(m);
			const originStr = origins
				.map(o => o.purchase_id ? 'OC #' + o.purchase_id : (o.order_id ? 'Orden #' + o.order_id : (o.bill_id ? 'Recibo #' + o.bill_id : '')))
				.join(', ');

			rows.push({
				ID: m.id,
				Monto: m.total,
				Moneda: m.currency_id,
				Fecha: m.paid_date ? Utils.getLocalMysqlStringFromDate(new Date(m.paid_date)).substring(0, 10) : '',
				Tipo: this.getTransactionTypeLabel(m.transaction_type),
				Referencia: m.reference || '',
				'ID Pago': m.payment_id || '',
				Origen: originStr,
				TipoMovimiento: m.type || '',
			});
		}

		const headers = ['ID', 'Monto', 'Moneda', 'Fecha', 'Tipo', 'Referencia', 'ID Pago', 'Origen', 'TipoMovimiento'];
		ExcelUtils.array2xlsx(rows, 'Movimientos_Bancarios.xlsx', headers);
	}
}
